import React, { forwardRef, useState } from 'react';

import { BUTTON_VARIANT_CLASSES } from '@app/constants/ui';
import { sleep } from '@app/libs/timer';
import { cn } from '@app/libs/utils';

import { Tooltip } from './Tooltip';

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  loadingText?: string | React.ReactNode;
  spinClassName?: string;
  loadingDelayMs?: number;
  loadingSize?: 'xs' | 'sm' | 'md' | 'lg';
  tooltip?: string | React.ReactNode;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  outline?: boolean;
  variant?: 'primary' | 'secondary' | 'danger' | 'success' | 'warning' | 'none' | 'white';
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      onClick,
      disabled,
      className,
      loadingText,
      spinClassName,
      loadingDelayMs,
      loadingSize = 'xs',
      tooltip,
      size,
      outline = false,
      variant = 'none',
      ...props
    },
    ref
  ) => {
    const [isLoading, setIsLoading] = useState(false);

    const handleClick: React.MouseEventHandler<HTMLButtonElement> = async e => {
      setIsLoading(true);
      try {
        if (onClick) {
          const result = onClick(e);
          const maybePromise: any = result as any;
          if (maybePromise && typeof maybePromise.then === 'function') await maybePromise;
          if (loadingDelayMs && loadingDelayMs > 0) await sleep(loadingDelayMs);
        }
      } finally {
        setIsLoading(false);
      }
    };

    const button = (
      <button
        ref={ref}
        onClick={handleClick}
        disabled={disabled || isLoading}
        className={cn(
          'flex items-center justify-center gap-2 px-4 py-2 transition-colors',
          className,
          !className?.match(/( |^)h-/) && 'h-10',
          !className?.match(/( |^)cursor-/) && 'cursor-pointer',
          !className?.match(/( |^)rounded-/) && 'rounded-lg',
          (disabled || isLoading) && 'cursor-not-allowed',
          outline &&
            !disabled &&
            BUTTON_VARIANT_CLASSES[variant as keyof typeof BUTTON_VARIANT_CLASSES].outline,
          outline &&
            disabled &&
            BUTTON_VARIANT_CLASSES[variant as keyof typeof BUTTON_VARIANT_CLASSES].outlineDisabled,
          disabled &&
            BUTTON_VARIANT_CLASSES[variant as keyof typeof BUTTON_VARIANT_CLASSES].disabled,
          isLoading &&
            BUTTON_VARIANT_CLASSES[variant as keyof typeof BUTTON_VARIANT_CLASSES].loading,
          !outline &&
            !disabled &&
            !isLoading &&
            BUTTON_VARIANT_CLASSES[variant as keyof typeof BUTTON_VARIANT_CLASSES].default,
          size === 'xs' && 'text-xs',
          size === 'sm' && 'text-sm',
          size === 'md' && 'text-md',
          size === 'lg' && 'text-lg'
        )}
        {...props}
      >
        {isLoading ? (
          <>
            <span
              className={cn(
                'inline-block',
                loadingSize === 'xs' && 'h-4 w-4 border-2',
                loadingSize === 'sm' && 'h-6 w-6 border-3',
                loadingSize === 'md' && 'h-8 w-8 border-4',
                loadingSize === 'lg' && 'h-10 w-10 border-5',
                'animate-spin rounded-full border-solid border-current border-e-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]',
                spinClassName
              )}
            ></span>
            <span>{loadingText ?? children}</span>
          </>
        ) : (
          children
        )}
      </button>
    );

    return tooltip ? (
      <Tooltip content={tooltip} variant={variant as any}>
        {button}
      </Tooltip>
    ) : (
      button
    );
  }
);

Button.displayName = 'Button';
